import { useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import { PaymentAndInsuranceKnowledge } from '@weave/schema-gen-ts/dist/schemas/ai-settings/v1/ai_settings.pb';
import cloneDeep from 'lodash-es/cloneDeep';
import isEqual from 'lodash-es/isEqual';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { LocationChips } from '@frontend/location-chips';
import { Button, Heading, Modal, ModalControlResponse, Text, useTooltip } from '@frontend/design-system';
import {
  AIRPaymentKnowledgeProvider,
  useAIRPaymentKnowledgeStore,
  selectLocationHasErrors,
} from '../../../../stores/air-settings.store';
import { AiReceptionistTrackingIds } from '../../../../tracking-ids';
import { DEFAULT_PAYMENT_KNOWLEDGE_DATA } from '../constants';
import { PaymentKnowledgeForm } from './payment-knowledge-form';

type Props = {
  modalControls: ModalControlResponse;
  filteredLocationIds: string[];
};

export const PaymentKnowledgeBulkEditModal = ({ modalControls, filteredLocationIds }: Props) => {
  const { draftSettings, updateBulkDraft, markFieldAsDeleted } = useAIRPaymentKnowledgeStore([
    'draftSettings',
    'updateBulkDraft',
    'markFieldAsDeleted',
  ]);

  const initialState = useMemo(() => {
    if (!modalControls.modalProps.show) {
      return {
        draftSettings: { bulk: cloneDeep(DEFAULT_PAYMENT_KNOWLEDGE_DATA) },
        initialSettings: { bulk: cloneDeep(DEFAULT_PAYMENT_KNOWLEDGE_DATA) },
      };
    }
    if (filteredLocationIds.length === 1) {
      const singleLocState = draftSettings[filteredLocationIds[0]] || DEFAULT_PAYMENT_KNOWLEDGE_DATA;
      return {
        draftSettings: { bulk: cloneDeep(singleLocState) },
        initialSettings: { bulk: cloneDeep(singleLocState) },
      };
    }

    const state: PaymentAndInsuranceKnowledge = {};
    filteredLocationIds.forEach((locId) => {
      const locData = draftSettings[locId];
      if (locData) {
        if (locData.paymentTypes !== undefined && state.paymentTypes === undefined) {
          state.paymentTypes = '';
        }
        if (locData.insuranceAcceptedInNetwork !== undefined && state.insuranceAcceptedInNetwork === undefined) {
          state.insuranceAcceptedInNetwork = [];
        }
        if (locData.insuranceAcceptedOutOfNetwork !== undefined && state.insuranceAcceptedOutOfNetwork === undefined) {
          state.insuranceAcceptedOutOfNetwork = [];
        }
        if (locData.insuranceNotAccepted !== undefined && state.insuranceNotAccepted === undefined) {
          state.insuranceNotAccepted = [];
        }
      }
    });
    return {
      draftSettings: { bulk: cloneDeep(state) },
      initialSettings: { bulk: cloneDeep(state) },
    };
  }, [modalControls.modalProps.show, filteredLocationIds, draftSettings]);

  const handleConfirm = useCallback(
    (data: PaymentAndInsuranceKnowledge, deletedFields: Array<keyof PaymentAndInsuranceKnowledge>) => {
      updateBulkDraft(filteredLocationIds, data);
      filteredLocationIds.forEach((locationId) => {
        deletedFields.forEach((field) => {
          markFieldAsDeleted(locationId, field);
        });
      });
    },
    [filteredLocationIds, updateBulkDraft, markFieldAsDeleted]
  );

  const presentFields = useMemo(() => {
    const present: Partial<Record<keyof PaymentAndInsuranceKnowledge, boolean>> = {};
    if (modalControls.modalProps.show) {
      filteredLocationIds.forEach((locId) => {
        const locData = draftSettings[locId];
        if (locData) {
          (Object.keys(locData) as Array<keyof PaymentAndInsuranceKnowledge>).forEach((key) => {
            if (
              locData[key] !== undefined &&
              locData[key] !== null &&
              locData[key] !== '' &&
              (!Array.isArray(locData[key]) || (locData[key] as Array<unknown>).length > 0)
            ) {
              present[key] = true;
            }
          });
        }
      });
    }
    return present as Record<keyof PaymentAndInsuranceKnowledge, boolean>;
  }, [draftSettings, filteredLocationIds, modalControls.modalProps.show]);

  return (
    <AIRPaymentKnowledgeProvider key={modalControls.modalProps.show ? 'open' : 'closed'} initialState={initialState}>
      <PaymentKnowledgeBulkEditModalContent
        modalControls={modalControls}
        filteredLocationIds={filteredLocationIds}
        onConfirm={handleConfirm}
        presentFields={presentFields}
      />
    </AIRPaymentKnowledgeProvider>
  );
};

const PaymentKnowledgeBulkEditModalContent = ({
  modalControls,
  filteredLocationIds,
  onConfirm,
  presentFields,
}: Props & {
  onConfirm: (data: PaymentAndInsuranceKnowledge, deletedFields: Array<keyof PaymentAndInsuranceKnowledge>) => void;
  presentFields: Record<keyof PaymentAndInsuranceKnowledge, boolean>;
}) => {
  const { t } = useTranslation('ai-receptionist-settings');
  const { draftSettings, initialSettings, deletedDraftFields, clearDraft, fieldErrors } = useAIRPaymentKnowledgeStore([
    'draftSettings',
    'initialSettings',
    'deletedDraftFields',
    'clearDraft',
    'fieldErrors',
  ]);

  const bulkHasErrors = useAIRPaymentKnowledgeStore(selectLocationHasErrors('bulk'));

  const {
    triggerProps: doneTriggerProps,
    tooltipProps: doneTooltipProps,
    Tooltip: DoneTooltip,
  } = useTooltip({
    placement: 'top',
  });

  const handleDone = () => {
    const localData = draftSettings['bulk'] || ({} as PaymentAndInsuranceKnowledge);
    const initialLocalData = initialSettings['bulk'] || ({} as PaymentAndInsuranceKnowledge);
    const localDeleted = deletedDraftFields['bulk'] || [];
    const localErrors = fieldErrors['bulk'] || {};

    const validData: PaymentAndInsuranceKnowledge = { ...localData };

    // Filter out fields that are NOT dirty or have errors
    const keys = Object.keys(validData) as Array<keyof PaymentAndInsuranceKnowledge>;
    keys.forEach((field) => {
      const hasError = localErrors[field];
      const isDirty = !isEqual(validData[field], initialLocalData[field]);

      // If the field is an empty array and was not present in the initial data, we can consider it as not dirty and remove it from the validData
      if (Array.isArray(validData[field]) && validData[field].length === 0 && initialLocalData[field] === undefined) {
        delete validData[field];
      }

      if (hasError || !isDirty) {
        delete validData[field];
      }
    });

    onConfirm(validData, localDeleted);
    modalControls.closeModal();
  };

  const onClose = () => {
    clearDraft();
    modalControls.closeModal();
  };

  return (
    <Modal {...modalControls.modalProps} minWidth={800}>
      <Modal.Header onClose={onClose}>
        <div
          css={css`
            display: flex;
            gap: var(--fabric-space-gap-sm);
          `}
        >
          <Heading level={2}>{t('Manage All')}</Heading>
          <LocationChips filteredLocationIds={filteredLocationIds} />
        </div>
      </Modal.Header>

      <Modal.Body>
        <PaymentKnowledgeForm locationId='bulk' isManageAll={true} presentFields={presentFields} />
      </Modal.Body>

      <Modal.Footer>
        <div css={styles.actionBar}>
          <Text
            size='medium'
            color='subdued'
            css={css`
              display: flex;
              align-items: center;
              gap: var(--fabric-space-gap-xxs);
            `}
          >
            {t("Click 'Done' to confirm changes only.")}
            <span
              {...doneTriggerProps}
              css={css`
                cursor: pointer;
                display: inline-flex;
              `}
            >
              <Icon color='subdued' name='info-small' />
            </span>
          </Text>
          <Button
            variant='secondary'
            onClick={onClose}
            trackingId={AiReceptionistTrackingIds.paymentsKnowledgeCancelButton}
          >
            {t('Cancel')}
          </Button>
          <Button
            onClick={handleDone}
            disabled={bulkHasErrors}
            trackingId={AiReceptionistTrackingIds.paymentsKnowledgeMultiDoneButton}
          >
            {t('Done')}
          </Button>
        </div>
        <DoneTooltip {...doneTooltipProps} style={{ ...doneTooltipProps.style, maxWidth: '280px' }}>
          {t("AI Receptionist's will only update when you click 'Save' after Manage All is closed.")}
        </DoneTooltip>
      </Modal.Footer>
    </Modal>
  );
};

const styles = {
  actionBar: css`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: var(--fabric-space-gap-sm);
  `,
};
